import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { removeLanguagePrefix, getLanguageFromPath } from '../utils/languageRouting';

const SEO = ({ page, preloadImages = [], structuredDataExtras = [], seoOverride = null, includeServiceSchema = true }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const currentLang = getLanguageFromPath(location.pathname);
  const normalizePath = (pathname) => {
    if (!pathname || pathname === '/') return '/';
    return pathname.endsWith('/') ? pathname : `${pathname}/`;
  };
  const currentPath = normalizePath(removeLanguagePrefix(location.pathname));
  const canonicalPath = normalizePath(location.pathname);
  
  // Base URL
  const baseUrl = 'https://costastone.es';
  
  // OG Image for social sharing
  const ogImage = `${baseUrl}/images/hero-actual-prod.webp`;
  
  // Get SEO data from translations
  const title = seoOverride?.title || t(`seo.${page}.title`);
  const description = seoOverride?.description || t(`seo.${page}.description`);
  const keywords = seoOverride?.keywords || t(`seo.${page}.keywords`);
  
  // Critical images to preload based on page
  const criticalImages = preloadImages.filter(Boolean);
  
  // Language codes
  const languages = [
    { code: 'en', path: currentPath },
    { code: 'es', path: `/es${currentPath === '/' ? '/' : currentPath}` },
    { code: 'pl', path: `/pl${currentPath === '/' ? '/' : currentPath}` },
    { code: 'sv', path: `/sv${currentPath === '/' ? '/' : currentPath}` }
  ];

  const ogLocales = {
    en: 'en_US',
    es: 'es_ES',
    pl: 'pl_PL',
    sv: 'sv_SE'
  };

  const serviceKeys = [
    'terraces',
    'poolSurrounds',
    'driveways',
    'stairs',
    'balconies',
    'walkways',
    'garages'
  ];

  const businessId = `${baseUrl}/#business`;
  const structuredDataGraph = [
    {
      '@type': 'HomeAndConstructionBusiness',
      '@id': businessId,
      name: 'Costa Stone',
      url: `${baseUrl}/`,
      logo: `${baseUrl}/images/costa-stone-logo-master.svg`,
      image: ogImage,
      description,
      telephone: '+34 639 11 20 19',
      email: 'info@costastone.es',
      priceRange: 'EUR EUR',
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '08:00',
          closes: '18:00'
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: 'Saturday',
          opens: '10:00',
          closes: '14:00'
        }
      ],
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Costa del Sol',
        addressRegion: 'Malaga',
        addressCountry: 'ES'
      },
      areaServed: ['Costa del Sol', 'Marbella', 'Estepona', 'Benahavis', 'Nueva Andalucia', 'Fuengirola', 'Mijas', 'Malaga', 'Benalmadena', 'Torremolinos', 'Sotogrande']
    },
    {
      '@type': 'WebSite',
      '@id': `${baseUrl}/#website`,
      name: 'Costa Stone',
      url: `${baseUrl}/`,
      inLanguage: currentLang,
      publisher: { '@id': businessId }
    },
    {
      '@type': 'WebPage',
      '@id': `${baseUrl}${canonicalPath}`,
      url: `${baseUrl}${canonicalPath}`,
      name: title,
      description,
      inLanguage: currentLang,
      isPartOf: { '@id': `${baseUrl}/#website` },
      about: { '@id': businessId }
    }
  ];

  if (canonicalPath !== '/') {
    structuredDataGraph.push({
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: t('nav.home'), item: `${baseUrl}/` },
        { '@type': 'ListItem', position: 2, name: title, item: `${baseUrl}${canonicalPath}` }
      ]
    });
  }

  if (includeServiceSchema && (page === 'home' || page === 'services')) {
    serviceKeys.forEach((key) => {
      const serviceName = t(`mockData.services.${key}.title`);
      structuredDataGraph.push({
        '@type': 'Service',
        name: serviceName,
        description: t(`mockData.services.${key}.description`),
        serviceType: serviceName,
        provider: { '@id': businessId },
        areaServed: 'Costa del Sol'
      });
    });
  }

  if (page === 'faq') {
    structuredDataGraph.push({
      '@type': 'FAQPage',
      mainEntity: Array.from({ length: 10 }, (_, index) => {
        const key = `q${index + 1}`;
        return {
          '@type': 'Question',
          name: t(`mockData.faq.${key}.question`),
          acceptedAnswer: {
            '@type': 'Answer',
            text: t(`mockData.faq.${key}.answer`)
          }
        };
      })
    });
  }

  if (structuredDataExtras.length) {
    structuredDataGraph.push(...structuredDataExtras);
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': structuredDataGraph
  };
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Language Meta */}
      <html lang={currentLang} />
      
      {/* Hreflang Tags */}
      {languages.map(({ code, path }) => (
        <link
          key={code}
          rel="alternate"
          hrefLang={code}
          href={`${baseUrl}${path}`}
        />
      ))}
      
      {/* X-Default for international users */}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={`${baseUrl}${currentPath}`}
      />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`${baseUrl}${canonicalPath}`} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1536" />
      <meta property="og:image:height" content="1024" />
      <meta property="og:image:alt" content="Luxury terrace with premium stone carpet flooring on Costa del Sol" />
      <meta property="og:site_name" content="Costa Stone" />
      <meta property="og:locale" content={ogLocales[currentLang] || 'en_US'} />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={`${baseUrl}${canonicalPath}`} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      {/* Preload Critical Images */}
      {criticalImages.map((imageUrl, index) => (
        <link
          key={index}
          rel="preload"
          as="image"
          href={imageUrl}
          type={imageUrl.endsWith('.webp') ? 'image/webp' : 'image/jpeg'}
        />
      ))}
    </Helmet>
  );
};

export default SEO;
